if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;

        this.syncElements();
        if (!this.elements.viewer) return;

        this.initialized = true;
        this.boundOnSlideChanged = debounce(this.onSlideChanged.bind(this), 500);
        this.elements.viewer.addEventListener('slideChanged', this.boundOnSlideChanged);

        this.addEventListener('click', this.onMediaNavDelegateClick.bind(this));

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;
            button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        this.syncElements();
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      syncElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.getVisibleElement('[id^="GalleryViewer"]'),
          thumbnails: this.getVisibleElement('[id^="GalleryThumbnails"]'),
        };
      }

      getVisibleElement(selector) {
        const elements = Array.from(this.querySelectorAll(selector));
        if (!elements.length) return null;

        return (
          elements.find((element) => {
            const rect = element.getBoundingClientRect();
            const styles = window.getComputedStyle(element);
            return (
              rect.width > 0 &&
              rect.height > 0 &&
              styles.display !== 'none' &&
              styles.visibility !== 'hidden'
            );
          }) || elements[elements.length - 1]
        );
      }

      getMediaItems() {
        const slider = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!slider) return [];

        return Array.from(slider.children).filter(
          (element) => element.matches('.product__media-item[data-media-id]')
        );
      }

      onMediaNavDelegateClick(event) {
        const button = event.target.closest('.product__media-slider-button');
        if (!button || !this.contains(button)) return;
        this.onMediaNavClick(event);
      }

      onMediaNavClick(event) {
        event.preventDefault();
        const button = event.currentTarget?.closest?.('.product__media-slider-button') || event.target.closest('.product__media-slider-button');
        if (!button) return;

        this.syncElements();
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;

        const currentIndex = mediaItems.findIndex((element) => element.classList.contains('is-active'));
        const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
        const direction = button.name === 'next' ? 1 : -1;
        const targetIndex = Math.max(0, Math.min(mediaItems.length - 1, safeCurrentIndex + direction));
        const targetMediaId = mediaItems[targetIndex]?.dataset.mediaId;

        if (!targetMediaId || targetIndex === safeCurrentIndex) return;
        this.setActiveMedia(targetMediaId, false);
      }

      setActiveMedia(mediaId, prepend) {
        this.syncElements();
        const mediaItems = this.getMediaItems();
        const activeMedia = mediaItems.find((element) => element.dataset.mediaId === mediaId) || mediaItems[0];
        if (!activeMedia) {
          return;
        }
        mediaItems.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (typeof this.elements.viewer?.resetPages === 'function') this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (!activeThumbnail) return;
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        const slider = this.elements.viewer?.slider;
        if (!slider) return;
        slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
